import React, { useState } from "react";
import axios from "axios";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CircularProgress from "@mui/material/CircularProgress";
import {
  API_integrate_toast,
  API_integrate_square,
  API_integrate_heartland_retail,
} from "../../../constants";
import { getDefaultAxiosConfig } from "../../../utilities/requests";

// The backend names the endpoint by key so the chat never carries a raw URL.
const URL_BY_KEY = {
  API_integrate_toast,
  API_integrate_square,
  API_integrate_heartland_retail,
};

const SECURITY_NOTE =
  "Sent over HTTPS to Rightwork's encrypted secret store. Never shared with the assistant.";

/**
 * Setup Assistant credential card. Credential values live only in this
 * component's state: they are posted straight to the POS integrate endpoint
 * and cleared on success. They are never passed to any callback, so they
 * never reach the chat transcript or the assistant.
 */
export default function PosCredentialCard({
  request,
  companyId,
  status = "pending",
  onStatusChange,
  onConnected,
}) {
  const fields = Array.isArray(request?.fields) ? request.fields : [];
  const [values, setValues] = useState(() =>
    Object.fromEntries(fields.map((field) => [field.key, ""]))
  );
  const [revealed, setRevealed] = useState({});
  const [helpOpen, setHelpOpen] = useState(false);
  const [error, setError] = useState(null);

  // Backend title is already imperative ("Connect Toast"); the provider
  // label ("Toast") is what the connected/error copy needs.
  const title = request?.title || "Connect POS";
  const providerLabel = title.replace(/^Connect\s+/i, "") || "POS";
  const helpSteps = Array.isArray(request?.help?.steps) ? request.help.steps : [];
  const portalLabel = request?.help?.portal_label;
  const submitUrl = URL_BY_KEY[request?.submit?.url_key];
  const isSubmitting = status === "submitting";
  const canSubmit =
    !isSubmitting && fields.every((field) => (values[field.key] || "").trim().length > 0);

  const setStatus = (nextStatus) => onStatusChange?.(request.request_id, nextStatus);
  // Field ids carry the request id so two cards in one transcript never
  // share a label target.
  const fieldId = (key) => `pos-credential-${request?.request_id || "request"}-${key}`;

  const handleDismiss = () => {
    // Drop typed values before the form unmounts so nothing lingers in state.
    setValues(Object.fromEntries(fields.map((field) => [field.key, ""])));
    setRevealed({});
    setStatus("dismissed");
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (!canSubmit) return;
    if (!submitUrl) {
      setError("This POS can't be connected from chat yet. Use the Integrations page instead.");
      return;
    }
    setError(null);
    setStatus("submitting");
    const bodyKeys = request?.submit?.body_keys || {};
    const body = { company_id: companyId };
    fields.forEach((field) => {
      body[bodyKeys[field.key] || field.key] = (values[field.key] || "").trim();
    });
    try {
      const response = await axios.post(submitUrl, body, await getDefaultAxiosConfig());
      const connected = Boolean(response?.data?.redux?.payload?.connection_successful);
      if (!connected) {
        setError("POS authentication failed. Please verify your credentials and try again.");
        setStatus("pending");
        return;
      }
      setValues(Object.fromEntries(fields.map((field) => [field.key, ""])));
      setRevealed({});
      setStatus("connected");
      onConnected?.(request.provider);
    } catch (err) {
      const serverMessage = err?.response?.data?.system?.message;
      if (err?.response?.status === 429) {
        setError(serverMessage || "Too many attempts. Please wait a moment and try again.");
      } else {
        setError(serverMessage || `Couldn't connect ${providerLabel}. Please try again.`);
      }
      setStatus("pending");
    }
  };

  if (status === "connected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm my-2 bg-green-50 border border-green-200 text-green-700">
        <CheckCircleIcon fontSize="small" className="text-green-600 shrink-0" />
        <span className="font-medium">{providerLabel} connected</span>
      </div>
    );
  }

  if (status === "dismissed") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Skipped for now</span>
      </div>
    );
  }

  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <LockIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">{title}</span>
      </div>

      {/* Content. A form so Enter submits with the same guard as Connect. */}
      <form onSubmit={handleSubmit}>
      <div className="px-3 py-3 space-y-3">
        {helpSteps.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setHelpOpen((open) => !open)}
              aria-expanded={helpOpen}
              className="text-sm text-violet-700 hover:text-violet-900 flex items-center gap-1"
            >
              {helpOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              How to get these
            </button>
            {helpOpen && (
              <div className="mt-1 text-sm text-gray-600 bg-white rounded p-2">
                {portalLabel && (
                  <div className="text-xs font-medium text-gray-500 mb-1">{portalLabel}</div>
                )}
                <ol className="list-decimal pl-4 space-y-1">
                  {helpSteps.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}

        {fields.map((field) => {
          const isSecret = Boolean(field.secret);
          const isRevealed = Boolean(revealed[field.key]);
          // Same input styling as InviteProposalCard / MessageProposalCard.
          return (
            <div key={field.key}>
              <label
                htmlFor={fieldId(field.key)}
                className="block text-xs font-medium text-gray-600 mb-0.5"
              >
                {field.label}
              </label>
              <div className="relative">
                <input
                  id={fieldId(field.key)}
                  data-testid={`pos-credential-${field.key}`}
                  type={isSecret && !isRevealed ? "password" : "text"}
                  value={values[field.key] || ""}
                  placeholder={field.placeholder || ""}
                  onChange={(event) =>
                    setValues((prev) => ({ ...prev, [field.key]: event.target.value }))
                  }
                  disabled={isSubmitting}
                  autoComplete={isSecret ? "new-password" : "off"}
                  data-lpignore="true"
                  data-1p-ignore=""
                  spellCheck={false}
                  className={`w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white disabled:bg-gray-50 ${
                    isSecret ? "pr-8" : ""
                  }`}
                />
                {isSecret && (
                  <button
                    type="button"
                    aria-label={isRevealed ? `Hide ${field.label}` : `Show ${field.label}`}
                    onClick={() =>
                      setRevealed((prev) => ({ ...prev, [field.key]: !isRevealed }))
                    }
                    className="absolute inset-y-0 right-1.5 flex items-center text-gray-500 hover:text-gray-700"
                  >
                    {isRevealed ? (
                      <VisibilityOffIcon style={{ fontSize: "18px" }} />
                    ) : (
                      <VisibilityIcon style={{ fontSize: "18px" }} />
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}

        <div className="flex items-start gap-1.5 text-xs text-gray-500">
          <LockIcon style={{ fontSize: "14px" }} className="mt-0.5 shrink-0" />
          <span>{SECURITY_NOTE}</span>
        </div>

        {error && (
          <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
        <button
          type="button"
          onClick={handleDismiss}
          disabled={isSubmitting}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Not now
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {isSubmitting && <CircularProgress size={14} sx={{ color: "white" }} />}
          Connect
        </button>
      </div>
      </form>
    </div>
  );
}
